import { AccountSuspendedScreen } from "@/components/account-suspended-screen"
import { AppSidebar } from "@/components/app-sidebar"
import {
  NoAdminAccessScreen,
  ServerUnreachableScreen,
} from "@/components/no-access-screen"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { Skeleton } from "@/components/ui/skeleton"
import { NotificationsProvider } from "@/contexts/notifications.context"
import { useMyPermissionsQuery } from "@/hooks/use-permissions"
import { hasAnyAdmin, NO_PERMISSIONS } from "@/lib/permissions"
import { authProvider } from "@/providers/auth.provider"
import { ForbiddenError } from "@/providers/base.provider"
import { safetyProvider, type MyEnforcementStatus } from "@/providers/safety.provider"
import { DASHBOARD_ROUTES, routeUrl } from "@/routes"
import { useAuthStore } from "@/stores/auth.store"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useState } from "react"
import { Navigate, Outlet, useLocation, useNavigate } from "react-router"
import { toast } from "sonner"

export function DashboardLayout() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const clearSession = useAuthStore((s) => s.clearSession)
  const [suspension, setSuspension] = useState<MyEnforcementStatus | null>(null)
  const [enforcementChecked, setEnforcementChecked] = useState(false)

  const {
    data: permissions,
    isLoading: permissionsLoading,
    error: permissionsError,
    refetch: refetchPermissions,
  } = useMyPermissionsQuery()
  // Only a real 403 means "denied". Anything else (backend down, 5xx) must not
  // be read as a suspension check, or as having no admin access.
  const permissionsDenied = permissionsError instanceof ForbiddenError
  const serverUnreachable = !!permissionsError && !permissionsDenied

  const checkEnforcement = useCallback(async () => {
    const status = await safetyProvider
      .getMyEnforcement()
      .catch(() => undefined)
    setSuspension(status ?? null)
    setEnforcementChecked(true)
    if (status === null) {
      // Ban was lifted (or a stray 403) — retry whatever failed.
      queryClient.invalidateQueries()
    }
  }, [queryClient])

  useEffect(() => {
    function handleUnauthorized() {
      clearSession()
      navigate("/auth", { replace: true })
    }
    // A 403 no longer means the session is gone. It's either a permission the
    // user doesn't have — the cached /me/permissions can lag by a minute — or
    // a suspension, which only shows up once /me/permissions is denied too.
    function handleForbidden() {
      toast.error("You don't have access to this.")
      refetchPermissions()
    }
    window.addEventListener("auth:unauthorized", handleUnauthorized)
    window.addEventListener("auth:forbidden", handleForbidden)
    return () => {
      window.removeEventListener("auth:unauthorized", handleUnauthorized)
      window.removeEventListener("auth:forbidden", handleForbidden)
    }
  }, [clearSession, navigate, refetchPermissions])

  // /me/permissions is open to every logged-in user, so being denied there
  // means the account itself is suspended.
  // Check once per denial: the invalidate above resets a data-less query to
  // pending, so re-checking on every flip back to denied loops forever.
  useEffect(() => {
    if (permissionsDenied && !enforcementChecked) checkEnforcement()
  }, [permissionsDenied, enforcementChecked, checkEnforcement])

  useEffect(() => {
    if (permissions) setEnforcementChecked(false)
  }, [permissions])

  async function logout() {
    await authProvider.logout().catch(() => {})
    clearSession()
    navigate("/auth", { replace: true })
  }

  if (!user) {
    return <Navigate to="/auth" replace />
  }

  if (suspension) {
    return <AccountSuspendedScreen enforcement={suspension} />
  }

  if (permissionsLoading || (permissionsDenied && !enforcementChecked)) {
    return (
      <div className="flex min-h-svh items-center justify-center p-6">
        <Skeleton className="h-32 w-full max-w-sm" />
      </div>
    )
  }

  if (serverUnreachable && !permissions) {
    return (
      <ServerUnreachableScreen
        onRetry={() => refetchPermissions()}
        onLogout={logout}
      />
    )
  }

  const me = permissions ?? NO_PERMISSIONS

  if (!hasAnyAdmin(me)) {
    return <NoAdminAccessScreen onLogout={logout} />
  }

  // The home screen runs on admin/users. Someone granted only, say, badges
  // would otherwise land on a locked page every time they log in.
  if (pathname === "/dashboard" && me.permissions["admin/users"] === "none") {
    const firstOpen = DASHBOARD_ROUTES.find(
      (r) => r.path && (!r.area || me.permissions[r.area] !== "none"),
    )
    if (firstOpen) return <Navigate to={routeUrl(firstOpen)} replace />
  }

  return (
    <NotificationsProvider>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <Outlet />
        </SidebarInset>
      </SidebarProvider>
    </NotificationsProvider>
  )
}
